import {
  ApprovalStatus,
  AssignmentCancelTypeEnum,
  AssignmentDeviationEnum,
  AssignmentModel,
  AssignmentStatusEnum,
  CaseEventType,
  CaseModel,
  CaseTypeEnum,
  EventModel,
} from 'api/model';
import { personalTransportAssignmentTypes } from 'constants/AppConstants';
import { UserJsonData } from '../EventsAndComments/eventComponents/types';

// Ärendetyper som använder "Uthyrd personal"-formuläret (inget fordon, ingen prissättning, inte
// kundvänd, default 8h30 + 30 min rast, "kopiera över flera dagar"). Samåkning delar detta med
// RentedStaff. Skillnaderna (separat Från/Till-adress, dold Kund/Kund tel) hanteras separat och
// ligger medvetet INTE i detta predikat.
export const isStaffRentalCaseType = (caseType: CaseTypeEnum) =>
  caseType === CaseTypeEnum.RentedStaff || caseType === CaseTypeEnum.Samakning;

// Ärendetyper utan fordon: regnr, miltal, elbil och övriga fordonsfält döljs och är frivilliga.
// Uthyrd personal-formulärets typer plus Däckkörning (som annars beter sig som Avvikande jobb).
export const isVehicleLessCaseType = (caseType: CaseTypeEnum) =>
  isStaffRentalCaseType(caseType) || caseType === CaseTypeEnum.Dackkorning;

// Ärendetyper där "Kopiera över flera dagar" ersätter den vanliga kopieringen.
export const replacesCopyWithCopyOverMultipleDays = (caseType: CaseTypeEnum) =>
  isStaffRentalCaseType(caseType) || caseType === CaseTypeEnum.Dackkorning;

// Ärendetyper vars uppdrag aldrig får fast tid, inte heller för hand av Efterkontroll/SuperAdmin.
// Motsvarar NeverHasFixedTime() i backend, som rensar värdet oavsett klient.
export const caseTypeNeverHasFixedTime = (caseType: CaseTypeEnum) =>
  caseType === CaseTypeEnum.Dackkorning;

// "Kopiera över flera dagar" tar med lördag och söndag för dessa typer, annars bara mån–fre.
export const copyOverMultipleDaysIncludesWeekends = (caseType: CaseTypeEnum) =>
  caseType === CaseTypeEnum.CarTransportTruck ||
  caseType === CaseTypeEnum.Dackkorning;

// Ärendetyper som beter sig som "Avvikande jobb" (RMOther): samma formulär, SMS-hantering,
// aldrig autogodkännande (utom Avyttringar och Däckkörning, och Avvikande jobb/Lånebilar för Riddermark-företag —
// se IsNeverAutoApproved/IsAutoApprovedOnlyForRiddermarkCompany i backend).
// Avyttringar/Garantiärenden/Åter till logistik är exakta
// kopior av RMOther med egna namn. Däckkörning likaså, men utan fordon (isVehicleLessCaseType).
// (De räknas dock INTE som avvikelse i avvikelse-KPI:n — det är enbart RMOther.)
export const isRmOtherLikeCaseType = (caseType: CaseTypeEnum) =>
  caseType === CaseTypeEnum.RMOther ||
  caseType === CaseTypeEnum.Avyttringar ||
  caseType === CaseTypeEnum.Garantiarenden ||
  caseType === CaseTypeEnum.AterTillLogistik ||
  caseType === CaseTypeEnum.Dackkorning;

// Ärendetyper som beter sig som Butiksleverans (FacilityToFacility). "Flytt mellan anläggningar"
// (FacilityTransfer) är en kopia med eget namn. Motsvarar IsFacilityToFacilityLike() i backend.
export const isFacilityToFacilityLikeCaseType = (caseType: CaseTypeEnum) =>
  caseType === CaseTypeEnum.FacilityToFacility ||
  caseType === CaseTypeEnum.FacilityTransfer;

// Typer utan fordon (Däckkörning) får inga SMS: texterna bygger på regnr, som saknas.
export const caseTypeShowsCustomerUpdateMessagesToggle = (
  caseType: CaseTypeEnum
) =>
  !isVehicleLessCaseType(caseType) &&
  (([
    CaseTypeEnum.Purchase,
    CaseTypeEnum.HomeDelivery,
    CaseTypeEnum.Auction,
  ] as CaseTypeEnum[]).includes(caseType) ||
    isFacilityToFacilityLikeCaseType(caseType) ||
    isRmOtherLikeCaseType(caseType));

export const caseTypeDefaultsToSendsCustomerUpdateMessages = (
  caseType: CaseTypeEnum
) =>
  (
  [
    CaseTypeEnum.Purchase,
    CaseTypeEnum.HomeDelivery,
    CaseTypeEnum.Auction,
    ] as CaseTypeEnum[]
  ).includes(caseType) || isFacilityToFacilityLikeCaseType(caseType);

const allStatusOptions: AssignmentStatusEnum[] =
  Object.values(AssignmentStatusEnum);

export const getSelectableStatusOptions = (
  currentStatus: AssignmentStatusEnum,
  currentCaseType: CaseTypeEnum,
  assignment?: AssignmentModel
): AssignmentStatusEnum[] =>
  allStatusOptions
    .sort((a, b) => a - b)
    .filter(
      (opt) =>
        // Show current status in the list, so that it's visible that it's selected
        opt === currentStatus ||
        (!(
          [
          /**
           * Approving, Aborting, Cancelling and Discarding assignments is only allowed via an action
           * from the Case view, in order to guide the user through the necessary steps.
           * */
          AssignmentStatusEnum.Approved,
          AssignmentStatusEnum.AbortedNotFinished,
          AssignmentStatusEnum.Aborted,
          AssignmentStatusEnum.CanceledNotFinished,
          AssignmentStatusEnum.Canceled,
          AssignmentStatusEnum.Inspected,
          ] as AssignmentStatusEnum[]
        ).includes(opt) &&
          // Hide AssignedToCarTransportTruck unless the assignment is loaded by a transport truck
          !(
            opt === AssignmentStatusEnum.AssignedToCarTransportTruck &&
            (!assignment?.loadedByTransport ||
              assignment.loadedByTransport.length === 0)
          )) ||
        // show AssignmentStatusEnum.Inspected when caseType is CaseTypeEnum.HomeDelivery
        (opt === AssignmentStatusEnum.Inspected &&
          currentCaseType === CaseTypeEnum.HomeDelivery)
    );

export type StatusOptionIsDisabledProps = {
  currentAssignmentStatus: AssignmentStatusEnum;
  events: EventModel[];
  currentAssignmentId?: number;
  currentAssignedToId?: number;
  drivingPolicyAssignmentInNeedOfHandling?: boolean;
};
export const statusOptionIsDisabled = ({
  currentAssignmentStatus,
  events,
  currentAssignedToId,
  currentAssignmentId,
  drivingPolicyAssignmentInNeedOfHandling,
}: StatusOptionIsDisabledProps) => {
  // return true for all options above AssignmentStatus.Assigned if there are CaseEvent.RequestAssignFieldTesterOutsideDrivingPolicy that have not been approved

  let relevantEvents = events;

  if (currentAssignmentId && currentAssignedToId) {
    // filter out events that are not related to the current assignment and user
    relevantEvents = relevantEvents.filter((c) => {
      let cUser;
      if (
        c.caseEventType ===
          CaseEventType.RequestAssignFieldTesterOutsideDrivingPolicy &&
        c.data
      ) {
        try {
          const data = JSON.parse(c.data) as UserJsonData;
          cUser = data.UserId;
        } catch (error) {
          // console.log('failed to parse data');
        }
      }
      return (
        c.assignmentID === currentAssignmentId && cUser === currentAssignedToId
      );
    });
  }

  if (currentAssignmentStatus >= AssignmentStatusEnum.Assigned) {
    // first, check if there are any approved requests for this assignment
    const dpRequests = relevantEvents.filter(
      (r) =>
        r.caseEventType ===
        CaseEventType.RequestAssignFieldTesterOutsideDrivingPolicy
    );

    if (dpRequests.length > 0) {
      // there are requests for the assignment, and they are all approved
      const result = dpRequests.some(
        (r) => r.approvalStatus !== ApprovalStatus.APPROVED
      );

      return result;
    }
    // if no requests have come for the assignment, check the drivingPolicyAssignmentInNeedOfHandling flag
    return drivingPolicyAssignmentInNeedOfHandling === undefined
      ? false
      : drivingPolicyAssignmentInNeedOfHandling;
  }
  return false;
};

const allCancelTypeOptions: AssignmentCancelTypeEnum[] = Object.values(
  AssignmentCancelTypeEnum
);

/**
 * Hemleveransen (i linkedCases, samma RideOrderID) räknas som avbokad när den har minst ett
 * fordonsben (ej persontransport, ej borttaget) och inget av dem längre är aktivt. Ett ombokat
 * ärende med ett nytt aktivt ben räknas alltså INTE som avbokat.
 * Speglar backend-regeln i AutoUpdateAssignmentsService.ValidateCancelTypeAgainstLinkedHomeDelivery.
 */
export const isLinkedHomeDeliveryCancelled = (
  linkedCases: CaseModel[] | undefined
): boolean => {
  const homeDelivery = linkedCases?.find(
    (c) => c.caseTypeID === CaseTypeEnum.HomeDelivery
  );
  if (!homeDelivery) return false;

  const vehicleLegs = homeDelivery.assignmentList.filter(
    (a) =>
      !personalTransportAssignmentTypes.includes(a.assignmentTypeID) &&
      a.assignmentDeviationID !== AssignmentDeviationEnum.Discarded
  );

  return (
    vehicleLegs.length > 0 &&
    vehicleLegs.every(
      (a) =>
        a.assignmentCancelTypeID != null ||
        (
          [
            AssignmentDeviationEnum.Canceled,
            AssignmentDeviationEnum.Aborted,
          ] as AssignmentDeviationEnum[]
        ).includes(a.assignmentDeviationID)
    )
  );
};

/**
 * "Avbok ej anlänt"/"Bom anlänt" får inte sättas på ett inbyte vars kopplade hemleverans är
 * avbokad — oavsett hemleveransens avbokningstyp. Backend blockerar också detta.
 */
export const arrivedCancelTypesBlockedByLinkedHomeDelivery = (
  caseType: CaseTypeEnum | undefined,
  linkedCases: CaseModel[] | undefined
): boolean =>
  caseType === CaseTypeEnum.TradeIn && isLinkedHomeDeliveryCancelled(linkedCases);

const canceledEquivalentStatuses = [
  AssignmentStatusEnum.Canceled,
  AssignmentStatusEnum.Aborted,
  AssignmentStatusEnum.Discarded,
] as AssignmentStatusEnum[];

/**
 * Ärendets huvuduppdrag: för hemleverans det SISTA benet (ut till kund), för alla andra
 * ärendetyper det FÖRSTA (hämtningen), i Bokad till-ordning. Persontransporter och
 * avbokade/bomkörda/borttagna ben räknas inte. Samma regel som AssignmentLogic.GetMainAssignment.
 */
export const getMainAssignment = (
  caseType: CaseTypeEnum,
  assignments: AssignmentModel[] | undefined
): AssignmentModel | undefined => {
  const eligible = (assignments ?? [])
    .filter(
      (a) =>
        !personalTransportAssignmentTypes.includes(a.assignmentTypeID) &&
        !canceledEquivalentStatuses.includes(a.assignmentStatusID)
    )
    .sort(
      (a, b) =>
        a.bookedTo.getTime() - b.bookedTo.getTime() ||
        a.assignmentID - b.assignmentID
    );

  return caseType === CaseTypeEnum.HomeDelivery
    ? eligible[eligible.length - 1]
    : eligible[0];
};

/**
 * Avbokas huvuduppdraget blir resten av ärendet inte av, så avbokningsmodalen bockar i ärendets
 * övriga uppdrag från start. Slutförda och godkända ben bockas inte i: avbokningen skriver över
 * deras status och sluttid.
 */
export const getOtherAssignmentIdsToCancelByDefault = (
  caseModel: CaseModel,
  assignmentId: number,
  otherAssignments: AssignmentModel[]
): number[] =>
  getMainAssignment(caseModel.caseTypeID, caseModel.assignmentList)
    ?.assignmentID === assignmentId
    ? otherAssignments
        .filter((a) => a.assignmentStatusID < AssignmentStatusEnum.Complete)
        .map((a) => a.assignmentID)
    : [];

const cancelOptionIsDisabled = (
  cancelOption: AssignmentCancelTypeEnum,
  assignment: AssignmentModel,
  blockArrivedCancelTypes: boolean
) => {
  switch (cancelOption) {
    case AssignmentCancelTypeEnum.CanceledDayBefore:
      // return true if assignment is already started
      return !!assignment.startTime;
    case AssignmentCancelTypeEnum.CanceledNotStartedSameDay:
      // return true if assignment is already started
      return !!assignment.startTime;
    case AssignmentCancelTypeEnum.CanceledNotArrived:
    case AssignmentCancelTypeEnum.CanceledArrived:
      return blockArrivedCancelTypes || !assignment.startTime;
    default:
      return false;
  }
};

type CancelTypeOption = {
  option: AssignmentCancelTypeEnum | undefined;
  disabled: boolean;
};
export const getSelectableCancelTypeOptions = (
  assignment: AssignmentModel,
  caseType?: CaseTypeEnum,
  linkedCases?: CaseModel[]
): CancelTypeOption[] => {
  const blockArrivedCancelTypes = arrivedCancelTypesBlockedByLinkedHomeDelivery(
    caseType,
    linkedCases
  );
  const options: CancelTypeOption[] = allCancelTypeOptions.map((opt) => {
    return {
      option: opt,
      disabled: cancelOptionIsDisabled(opt, assignment, blockArrivedCancelTypes),
    };
  });
  // add an undefined option to the start of the list
  options.unshift({ option: undefined, disabled: false });
  return options;
};
